import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Lock, Loader2, ArrowRight, ShieldCheck, Eye, EyeOff, Check, X as XIcon } from "lucide-react";
import { CardWatchMark } from "@/components/CardWatchLogo";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/reset_password`,
        { token, password },
        { withCredentials: true }
      );
      await refresh();
      toast.success("Password updated · you're signed in");
      const firstTime = res.data?.first_time;
      navigate(firstTime ? "/onboarding" : "/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset link is invalid or expired");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B19] text-white relative overflow-hidden">
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/[0.08] blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/25 blur-[160px]" />

      <header className="relative z-10 border-b border-white/5">
        <div className="w-full max-w-4xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/signup" className="flex items-center gap-3 group" data-testid="reset-back-link">
            <ArrowLeft size={16} className="text-slate-500 group-hover:text-white transition-colors" />
            <div className="flex items-center gap-3">
              <CardWatchMark size={30} />
              <span
                className="text-white font-semibold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16 }}
              >
                Card<span style={{ color: "#00E676" }}>Watch</span>
              </span>
            </div>
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-md mx-auto px-6 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-3xl border border-white/8 bg-[#0B1226]/70 backdrop-blur-xl p-8"
          data-testid="reset-card"
        >
          <div className="w-11 h-11 rounded-xl bg-[#00E676]/[0.08] border border-[#00E676]/25 flex items-center justify-center">
            <Lock size={18} className="text-[#00E676]" />
          </div>
          <h1
            className="mt-5 text-white text-3xl font-bold tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="reset-title"
          >
            Choose a new password
          </h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            Once set, you'll be signed straight in. All existing sessions will be
            revoked for your security.
          </p>

          {!token ? (
            <div
              className="mt-6 rounded-xl border border-red-500/30 bg-red-500/[0.08] px-4 py-3 text-sm text-red-200"
              data-testid="reset-missing-token"
            >
              This link is missing its reset token. Please request a fresh one from{" "}
              <Link to="/forgot-password" className="underline hover:text-white">
                Forgot password
              </Link>
              .
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (8+ characters)"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  data-testid="reset-password-input"
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 pr-12 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676]/60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="reset-password-toggle"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-300 hover:text-white transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-[#00E676]/40"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  data-testid="reset-password-confirm"
                  className={`w-full rounded-xl border bg-black/20 px-4 py-3 pr-12 text-sm text-white placeholder-slate-500 focus:outline-none transition-colors ${
                    confirm && password === confirm
                      ? "border-[#00E676]/60 focus:border-[#00E676]"
                      : confirm && password !== confirm
                      ? "border-red-500/50 focus:border-red-400"
                      : "border-white/10 focus:border-[#00E676]/60"
                  }`}
                />
                {confirm && (
                  <span
                    className={`absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-full ${
                      password === confirm ? "bg-[#00E676]/15" : "bg-red-500/15"
                    }`}
                    data-testid="reset-password-match-indicator"
                    aria-hidden="true"
                  >
                    {password === confirm ? (
                      <Check size={13} className="text-[#00E676]" strokeWidth={2.6} />
                    ) : (
                      <XIcon size={13} className="text-red-400" strokeWidth={2.6} />
                    )}
                  </span>
                )}
              </div>
              {confirm && (
                <div
                  className={`-mt-1.5 text-[11px] font-semibold ${
                    password === confirm ? "text-[#00E676]" : "text-red-400"
                  }`}
                  data-testid="reset-password-match-label"
                >
                  {password === confirm ? "Passwords match" : "Passwords don't match yet"}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="reset-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] hover:bg-[#00E676]/[0.16] hover:border-[#00E676] text-[#00E676] font-semibold px-6 py-3 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Update password <ArrowRight size={14} /></>}
              </button>

              <div className="pt-2 text-[11px] text-slate-500 flex items-start gap-1.5">
                <ShieldCheck size={12} className="mt-0.5 text-[#00E676] flex-shrink-0" />
                Your reset link is one-time-use and expires in 60 minutes.
              </div>
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ResetPassword;
